import { useEffect, useRef } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import { auth, db } from "../firebase/config";
import {
  IMPORTANT_APP_EVENTS,
  LOW_COST_MODE,
  shouldSkipMinorAnalyticsEvent,
  trackDailyRollup,
} from "./lowCostAnalytics";
import { recordAnalyticsEvent, LEGACY_FIRST_ACTION_EVENTS } from "./analyticsV2";
import { isLegacyAnalyticsEnabled } from "./analyticsLegacyFlags";

export const APP_ANALYTICS_EVENT_TYPES = [
  "page_view",
  "signup_completed",
  "login_completed",
  "onboarding_started",
  "onboarding_step_completed",
  "onboarding_completed",
  "profile_started",
  "profile_completed",
  "add_product_started",
  "add_product_completed",
  "add_product_abandoned",
  "farm_stand_started",
  "farm_stand_completed",
  "farm_stand_abandoned",
  "stand_create_failed",
  "job_post_started",
  "job_post_completed",
  "job_post_abandoned",
  "farm_records_opened",
  "farm_records_first_action",
  "community_post_started",
  "community_post_completed",
  "community_post_abandoned",
  "community_post_create_failed",
];

const METADATA_KEYS = [
  "feature",
  "action",
  "status",
  "flowId",
  "relatedId",
  "role",
  "step",
  "source",
  "routeName",
  "completionPercent",
  "itemCount",
  "errorCode",
  "errorSummary",
];

const LAST_PAGE_VIEW_KEY = "mfp_last_app_page_view";
const ACTIVE_FLOWS_KEY = "mfp_app_active_flows";
const PAGE_VIEW_DEDUPE_MS = 3000;
const MIN_ABANDON_MS = 5000;

const sanitizeString = (value = "", maxLength = 160) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, maxLength);

const sanitizeUserAgent = () => {
  if (typeof navigator === "undefined") return "";
  const ua = navigator.userAgent || "";
  const browser =
    ua.includes("Edg/") ? "Edge" :
    ua.includes("Chrome/") ? "Chrome" :
    ua.includes("Firefox/") ? "Firefox" :
    ua.includes("Safari/") ? "Safari" :
    "Browser";
  const platform = navigator.platform || "";
  return sanitizeString([browser, platform].filter(Boolean).join(" / "), 120);
};

const safePath = (path = "/") => {
  const value = sanitizeString(path, 200);
  return value.startsWith("/") ? value : "/";
};

const sanitizeMetadata = (metadata = {}) => {
  const clean = {};

  METADATA_KEYS.forEach((key) => {
    if (!(key in metadata)) return;
    const value = metadata[key];

    if (typeof value === "string") {
      clean[key] = sanitizeString(value, 160);
    } else if (typeof value === "number" && Number.isFinite(value)) {
      clean[key] = Math.max(0, Math.min(Math.round(value), 1000000));
    } else if (typeof value === "boolean") {
      clean[key] = value;
    }
  });

  return clean;
};

const getReferrer = () => {
  if (typeof document === "undefined") return "";
  try {
    const referrer = document.referrer || "";
    if (!referrer) return "";
    const parsed = new URL(referrer);
    return sanitizeString(`${parsed.origin}${parsed.pathname}`, 220);
  } catch (error) {
    return "";
  }
};

export const trackAppEvent = async (eventType, metadata = {}) => {
  if (typeof window === "undefined") return false;
  if (!APP_ANALYTICS_EVENT_TYPES.includes(eventType)) return false;

  const user = auth.currentUser;
  const isAnonymous = !user?.uid;
  if (eventType === "signup_completed") {
    recordAnalyticsEvent("registration_complete", {
      feature: metadata.feature || "registration",
      action: metadata.action || "complete",
      path: window.location.pathname || "/",
    });
  } else if (LEGACY_FIRST_ACTION_EVENTS.includes(eventType)) {
    recordAnalyticsEvent("first_action", {
      feature: metadata.feature || "activation",
      action: metadata.action || eventType,
      path: window.location.pathname || "/",
    });
  } else if (eventType === "page_view") {
    recordAnalyticsEvent("page_view", {
      feature: metadata.feature || "navigation",
      path: window.location.pathname || "/",
    });
  }

  if (LOW_COST_MODE) {
    if (eventType === "page_view") {
      if (isLegacyAnalyticsEnabled("appDailyRollups")) {
        trackDailyRollup("page_view", { oncePerDay: true });
      }
      return false;
    }

    if (shouldSkipMinorAnalyticsEvent(eventType, isAnonymous)) {
      return false;
    }

    if (IMPORTANT_APP_EVENTS.has(eventType)) {
      if (isLegacyAnalyticsEnabled("appDailyRollups")) {
        trackDailyRollup(eventType);
      }
    }
  }

  if (!isLegacyAnalyticsEnabled("appAnalyticsEvents")) {
    return false;
  }

  const payload = {
    eventType,
    userId: user?.uid || null,
    isAnonymous,
    createdAt: serverTimestamp(),
    path: safePath(window.location.pathname || "/"),
    referrer: getReferrer(),
    userAgent: sanitizeUserAgent(),
    metadata: sanitizeMetadata(metadata),
  };

  try {
    await addDoc(collection(db, "analyticsEvents"), payload);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("App analytics event was not recorded:", error?.code || error?.message);
    }
    return false;
  }
};

const readFlows = () => {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(ACTIVE_FLOWS_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeFlows = (flows = []) => {
  try {
    window.sessionStorage.setItem(ACTIVE_FLOWS_KEY, JSON.stringify(flows.slice(-20)));
  } catch (error) {}
};

export const startAppFlow = (flowId, startEventType, metadata = {}) => {
  if (typeof window === "undefined" || !flowId) return;
  const flows = readFlows().filter((flow) => flow.flowId !== flowId);
  flows.push({
    flowId,
    abandonEventType: metadata.abandonEventType || "",
    startedAt: Date.now(),
    metadata: sanitizeMetadata({ ...metadata, flowId }),
  });
  writeFlows(flows);
  trackAppEvent(startEventType, { ...metadata, flowId });
};

export const completeAppFlow = (flowId, completeEventType, metadata = {}) => {
  if (typeof window === "undefined" || !flowId) return;
  writeFlows(readFlows().filter((flow) => flow.flowId !== flowId));
  trackAppEvent(completeEventType, { ...metadata, flowId });
};

export const abandonAppFlow = (flowId) => {
  if (typeof window === "undefined" || !flowId) return;
  const flows = readFlows();
  const flow = flows.find((item) => item.flowId === flowId);
  writeFlows(flows.filter((item) => item.flowId !== flowId));
  if (!flow?.abandonEventType) return;
  if (Date.now() - Number(flow.startedAt || Date.now()) < MIN_ABANDON_MS) return;
  trackAppEvent(flow.abandonEventType, {
    ...flow.metadata,
    status: "abandoned",
  });
};

export const AppPageAnalyticsTracker = () => {
  const location = useLocation();
  const previousPathRef = useRef("");

  useEffect(() => {
    const path = safePath(location.pathname || "/");
    const now = Date.now();
    const key = `${path}|${location.search || ""}`;

    try {
      const previous = JSON.parse(window.sessionStorage.getItem(LAST_PAGE_VIEW_KEY) || "null");
      if (previous?.key === key && now - Number(previous.at || 0) < PAGE_VIEW_DEDUPE_MS) {
        return;
      }
      window.sessionStorage.setItem(LAST_PAGE_VIEW_KEY, JSON.stringify({ key, at: now }));
    } catch (error) {}

    previousPathRef.current = path;
    if (LOW_COST_MODE) {
      trackDailyRollup("page_view", { oncePerDay: true });
      return;
    }

    trackAppEvent("page_view", {
      routeName: path,
      source: "route_change",
    });
  }, [location.pathname, location.search]);

  return null;
};

export const calculateProfileCompletion = ({
  profile = {},
  products = [],
  farmStands = [],
  jobs = [],
  communityPosts = [],
} = {}) => {
  const role = profile.role || profile.type || "";
  const hasLocation = Boolean(
    profile.city ||
      profile.county ||
      profile.state ||
      profile.locationText ||
      profile.location ||
      profile.lat
  );
  const checklist = [
    {
      id: "photo",
      label: "Profile photo/avatar",
      complete: Boolean(profile.profilePicture || profile.photoURL || profile.avatarUrl),
    },
    {
      id: "name",
      label: "Display name or farm name",
      complete: Boolean(profile.farmName || profile.businessName || profile.displayName || profile.name),
    },
    { id: "location", label: "Broad location", complete: hasLocation },
    { id: "bio", label: "Bio/about", complete: Boolean(profile.bio || profile.intro || profile.about) },
    { id: "role", label: "Role selected", complete: Boolean(role) },
  ];

  const roleLower = String(role).toLowerCase();
  let firstAction = {
    id: "first_action",
    label: "First post or listing",
    complete: products.length > 0 || farmStands.length > 0 || jobs.length > 0 || communityPosts.length > 0,
  };

  if (["farmer", "homesteader"].includes(roleLower)) {
    firstAction = {
      id: "first_product",
      label: "First product, stand, or community intro",
      complete: products.length > 0 || farmStands.length > 0 || communityPosts.length > 0,
    };
  } else if (roleLower === "landowner") {
    firstAction = {
      id: "first_job",
      label: "First field/day job or community intro",
      complete: jobs.length > 0 || communityPosts.length > 0,
    };
  } else if (roleLower === "day_worker") {
    firstAction = {
      id: "worker_intro",
      label: "Community intro or profile details",
      complete: communityPosts.length > 0 || Boolean(profile.bio || profile.intro),
    };
  } else if (roleLower === "community") {
    firstAction = {
      id: "community_intro",
      label: "Community intro",
      complete: communityPosts.length > 0,
    };
  }

  checklist.push(firstAction);
  const completed = checklist.filter((item) => item.complete).length;
  return {
    checklist,
    completed,
    total: checklist.length,
    percent: Math.round((completed / checklist.length) * 100),
  };
};
